import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, BadgeCheck } from "lucide-react";
import { adminApi, adminToken, errMsg } from "@/lib/api";
import { TypeBadge } from "@/components/TypeBadge";
import { BonusForm } from "@/components/admin/BonusForm";
import { DraftsPanel } from "@/components/admin/DraftsPanel";

const Kpi = ({ label, value, testid, gold }) => (
  <div className="rounded-2xl bg-[#0F131D] border border-white/10 p-5">
    <p className="text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`font-mono text-3xl font-bold mt-2 ${gold ? "gold-text" : "text-white"}`} data-testid={testid}>{value}</p>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [bonuses, setBonuses] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const api = adminApi();
      const [s, b, d] = await Promise.all([api.get("/admin/stats"), api.get("/admin/bonuses"), api.get("/admin/drafts")]);
      setStats(s.data); setBonuses(b.data); setDrafts(d.data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin/login"); }
    }
  }, [navigate]);

  useEffect(() => { if (!adminToken()) navigate("/admin/login"); else load(); }, [load, navigate]);

  const save = async (f) => {
    try {
      const api = adminApi();
      if (editing?.id) await api.put(`/admin/bonuses/${editing.id}`, f); else await api.post("/admin/bonuses", f);
      toast.success("Offer saved"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => { await adminApi().delete(`/admin/bonuses/${id}`); toast.success("Offer deleted"); load(); };
  const verifyAll = async () => { const { data } = await adminApi().post("/admin/bonuses/mark-verified"); toast.success(`${data.updated} offers marked verified today`); load(); };
  const logout = () => { localStorage.removeItem("admin_token"); navigate("/admin/login"); };

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/" className="text-xs text-slate-500 hover:text-emerald-400">← Public site</Link>
          <h1 className="font-display font-black text-3xl sm:text-4xl mt-1">Command center</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"><LogOut size={16} />Log out</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Kpi label="Revenue" value={`$${(stats?.revenue ?? 0).toFixed(2)}`} testid="admin-sales-total" gold />
        <Kpi label="Sales" value={stats?.sales_count ?? 0} testid="admin-sales-count" />
        <Kpi label="Paid customers" value={stats?.customers ?? 0} testid="admin-customers-count" />
        <Kpi label="Live offers" value={stats?.bonus_count ?? 0} testid="admin-bonus-count" />
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6 mt-8">
        <DraftsPanel drafts={drafts} reload={load} />
        <section className="rounded-2xl bg-[#0F131D] border border-white/10 p-6" data-testid="recent-sales-panel">
          <h2 className="font-display font-bold text-lg">Recent sales</h2>
          <div className="mt-4 space-y-2 text-sm">
            {stats?.recent_sales.map((s, i) => (
              <div key={i} className="flex justify-between border-b border-white/5 pb-2" data-testid={`sale-row-${i}`}>
                <span className="text-slate-300 truncate">{s.email}</span><span className="font-mono text-emerald-400">${s.amount.toFixed(2)}</span>
              </div>
            ))}
            {!stats?.recent_sales.length && <p className="text-slate-500">No sales yet.</p>}
          </div>
        </section>
      </div>

      <section className="rounded-2xl bg-[#0F131D] border border-white/10 p-6 mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display font-bold text-lg">All offers</h2>
          <div className="flex gap-2">
            <button onClick={verifyAll} data-testid="admin-verify-all-btn" className="flex items-center gap-2 rounded-full border border-white/10 px-4 h-10 text-sm hover:border-emerald-500/40"><BadgeCheck size={16} />Mark all verified today</button>
            <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-add-bonus-btn" className="flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-bold px-4 h-10 text-sm"><Plus size={16} />Add offer</button>
          </div>
        </div>
        <div className="mt-5 divide-y divide-white/5">
          {bonuses.map((b, i) => (
            <div key={b.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`admin-bonus-row-${i}`}>
              <TypeBadge type={b.bonus_type} />
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-sm">{b.casino} {!b.active && <span className="text-xs text-slate-500">(hidden)</span>}</p>
                <p className="text-xs text-slate-400">{b.title}</p>
              </div>
              <span className="font-mono text-xs text-slate-500">{b.clicks} clicks</span>
              <span className="font-mono text-amber-300 text-sm w-24 text-right">{b.value}</span>
              <button onClick={() => { setEditing(b); setOpen(true); }} data-testid={`admin-edit-bonus-${i}`} className="h-9 w-9 grid place-items-center rounded-full hover:bg-white/5"><Pencil size={15} /></button>
              <button onClick={() => remove(b.id)} data-testid={`admin-delete-bonus-${i}`} className="h-9 w-9 grid place-items-center rounded-full text-red-400 hover:bg-red-500/10"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </section>

      <BonusForm open={open} onOpenChange={setOpen} initial={editing} onSave={save} />
    </div>
  );
}
